import axios from 'axios';
import { isProductionPartnerHost } from '../../utils/partnerDomains';
import type {
  GetCaregiversParams,
  GetCaregiversResponse,
  GetCaregiverCalendarParams,
  GetCaregiverCalendarResponse,
  GetCarePlanAssignmentResponse
} from '../lib/types/api.types';

// Determine base URL based on environment
const getBaseURL = () => {
  const hostname = window.location.hostname;

  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname === '::1'
  ) {
    // Local testing
    return 'https://homenursing-api-prod.happypebble-41284363.southindia.azurecontainerapps.io';
  }
  if (isProductionPartnerHost(hostname)) {
    return 'https://homenursing-api-prod.happypebble-41284363.southindia.azurecontainerapps.io';
  } else if (hostname.includes('partner-dev.dardoc.com')) {
    return 'https://api-dev.homenursing.dardoc.com';
  } else {
    // Local development
    return 'https://homenursing-api-prod.happypebble-41284363.southindia.azurecontainerapps.io';
  }
};

export const HOME_NURSING_BASE_URL = getBaseURL();
export const HOME_NURSING_IS_DEV =
  import.meta.env.DEV // && !HOME_NURSING_BASE_URL.startsWith('https://homenursing-api-prod.happypebble-41284363.southindia.azurecontainerapps.io');

// Create axios instance for Home Nursing APIs
export const homeNursingApi = axios.create({
  baseURL: HOME_NURSING_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Log API calls in development only
if (import.meta.env.DEV) {
  homeNursingApi.interceptors.request.use(
    (config) => {
      const method = (config.method || 'GET').toUpperCase();
      const url = `${config.baseURL || ''}${config.url || ''}`;
      console.debug('[HomeNursing API]', method, url, {
        params: config.params,
        data: config.data,
      });
      return config;
    },
    (error) => {
      console.debug('[HomeNursing API] request error', error);
      return Promise.reject(error);
    }
  );

  homeNursingApi.interceptors.response.use(
    (response) => {
      const method = (response.config.method || 'GET').toUpperCase();
      const url = `${response.config.baseURL || ''}${response.config.url || ''}`;
      console.debug('[HomeNursing API]', method, url, '→', response.status);
      return response;
    },
    (error) => {
      const method = (error.config?.method || 'GET').toUpperCase();
      const url = `${error.config?.baseURL || ''}${error.config?.url || ''}`;
      console.debug('[HomeNursing API] error', method, url, error.response?.status, error.message);
      return Promise.reject(error);
    }
  );
}

// For now, hardcode serviceModel for testing
// TODO: Get this from auth system when backend adds the field
export const DEFAULT_SERVICE_MODEL = 'DARDOC_DIRECT';

// API endpoint functions
export const homeNursingService = {
  // Get caregivers list
  getCaregivers: async (params?: GetCaregiversParams): Promise<GetCaregiversResponse> => {
    const response = await homeNursingApi.get('/public/caregivers', { params });
    return response.data;
  },

  // Get caregiver calendar
  getCaregiverCalendar: async (
    caregiverId: string,
    params: GetCaregiverCalendarParams
  ): Promise<GetCaregiverCalendarResponse> => {
    const response = await homeNursingApi.get(`/public/caregivers/${caregiverId}/calendar`, { params });
    return response.data;
  },

  // Get care profiles (patients)
  getCareProfiles: async (params?: {
    status?: string;
    city?: string;
    search?: string;
  }) => {
    const response = await homeNursingApi.get('/care-profiles', { params });
    return response.data;
  },

  // Get care plan assignment
  getCarePlanAssignment: async (carePlanId: string): Promise<GetCarePlanAssignmentResponse> => {
    const response = await homeNursingApi.get(`/public/care-plans/${carePlanId}/assignment`);
    return response.data;
  },
};
